import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import HelpButton from "@/help/HelpButton";
import HelpPanel from "@/help/HelpPanel";
import HelpChatView from "@/help/HelpChatView";
import type { ApuriContext } from "@/help/HelpChatView";
import {
  APURI_HAS_AUTO_OPENED_KEY,
  APURI_IS_CLOSED_KEY,
  APURI_IS_EXPANDED_KEY,
  readStoredBool,
  shouldAutoOpenApuri,
  toStoredBool,
} from "@/help/helpWidgetState";
import {
  getHelpMode,
  getHelpRouteKey,
  getQuickQuestions,
} from "@/help/helpPlaybook";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
const APURI_LANDING_SCROLL_THRESHOLD = 280;
let hasCheckedAutoOpenInLifecycle = false;

const getFocusable = (container: HTMLElement): HTMLElement[] =>
  Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((element) => {
    if (element.hasAttribute("disabled")) return false;
    if (element.getAttribute("aria-hidden") === "true") return false;
    return true;
  });

export default function HelpWidget() {
  const { user, profile } = useAuth();
  const location = useLocation();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [canShowOnLanding, setCanShowOnLanding] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.scrollY > APURI_LANDING_SCROLL_THRESHOLD;
  });

  // Enriched context for AI chat
  const [chatContext, setChatContext] = useState<Omit<ApuriContext, "page" | "isSignedIn">>({});

  useEffect(() => {
    if (!user) {
      setChatContext({});
      return;
    }

    let cancelled = false;

    (async () => {
      const ctx: Omit<ApuriContext, "page" | "isSignedIn"> = {
        userName: profile?.name ?? null,
        plan: profile?.plan ?? null,
        subscriptionActive: profile?.subscription_active ?? false,
        credits: profile?.credits ?? 0,
      };

      // Fetch trip count
      const { count: tripCount } = await supabase
        .from("trips")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id);
      if (!cancelled) ctx.tripCount = tripCount ?? 0;

      // Fetch report count
      const { count: reportCount } = await supabase
        .from("reports")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id);
      if (!cancelled) ctx.reportCount = reportCount ?? 0;

      // Fetch organization membership
      const { data: membership } = await supabase
        .from("memberships")
        .select("organization_id, role")
        .eq("user_id", user.id)
        .limit(1)
        .maybeSingle();

      if (!cancelled && membership) {
        ctx.orgRole = membership.role;
        ctx.hasOrganization = true;

        const { data: org } = await supabase
          .from("organizations")
          .select("name, plan, credits, seat_limit, subscription_active")
          .eq("id", membership.organization_id)
          .single();

        if (!cancelled && org) {
          ctx.orgName = org.name;
          ctx.orgPlan = org.plan;
          ctx.orgCredits = org.credits;
          ctx.orgSeatLimit = org.seat_limit;

          const { count: memberCount } = await supabase
            .from("memberships")
            .select("id", { count: "exact", head: true })
            .eq("organization_id", membership.organization_id);
          if (!cancelled) ctx.orgMemberCount = memberCount ?? 0;
        }
      } else if (!cancelled) {
        ctx.hasOrganization = false;
      }

      if (!cancelled) setChatContext(ctx);
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, profile?.plan, profile?.credits, profile?.subscription_active]);

  const apuriContext = useMemo<ApuriContext>(() => ({
    page: location.pathname,
    isSignedIn: Boolean(user),
    ...chatContext,
  }), [location.pathname, user, chatContext]);

  const isCheckoutPath = location.pathname.startsWith("/checkout");

  const mode = getHelpMode(Boolean(user));
  const routeKey = getHelpRouteKey(location.pathname);

  const quickQuestionLabels = useMemo(
    () => getQuickQuestions(mode, routeKey).map((q) => q.label),
    [mode, routeKey]
  );

  const persistWidgetState = useCallback((next: { isOpen: boolean; isClosed: boolean }) => {
    try {
      localStorage.setItem(APURI_IS_EXPANDED_KEY, toStoredBool(next.isOpen));
      localStorage.setItem(APURI_IS_CLOSED_KEY, toStoredBool(next.isClosed));
    } catch {
      // Local storage can fail in privacy modes; ignore and continue.
    }
  }, []);

  useEffect(() => {
    try {
      const closed = readStoredBool(localStorage.getItem(APURI_IS_CLOSED_KEY), false);
      const expanded = readStoredBool(localStorage.getItem(APURI_IS_EXPANDED_KEY), false);
      setIsClosed(closed);
      setIsOpen(!closed && expanded);
    } catch {
      // Ignore unavailable storage.
    }
  }, []);

  useEffect(() => {
    if (!isCheckoutPath) return;
    setIsOpen(false);
  }, [isCheckoutPath]);

  useEffect(() => {
    if (hasCheckedAutoOpenInLifecycle) return;
    if (routeKey !== "landing") return;
    hasCheckedAutoOpenInLifecycle = true;

    try {
      const hasAutoOpened = readStoredBool(localStorage.getItem(APURI_HAS_AUTO_OPENED_KEY), false);
      const closed = readStoredBool(localStorage.getItem(APURI_IS_CLOSED_KEY), false);
      const canAutoOpen = shouldAutoOpenApuri({
        routeKey,
        hasCheckedInLifecycle: false,
        hasAutoOpened,
        isClosed: closed,
      });
      if (canAutoOpen) {
        localStorage.setItem(APURI_HAS_AUTO_OPENED_KEY, toStoredBool(true));
        localStorage.setItem(APURI_IS_CLOSED_KEY, toStoredBool(false));
        localStorage.setItem(APURI_IS_EXPANDED_KEY, toStoredBool(false));
        setIsClosed(false);
        setIsOpen(false);
      }
    } catch {
      // Ignore unavailable storage.
    }
  }, [routeKey]);

  const openPanel = useCallback(() => {
    setIsOpen(true);
    setIsClosed(false);
    persistWidgetState({ isOpen: true, isClosed: false });
  }, [persistWidgetState]);

  const closePanel = useCallback(
    (setClosed = true) => {
      setIsOpen(false);
      setIsClosed(setClosed);
      persistWidgetState({ isOpen: false, isClosed: setClosed });
    },
    [persistWidgetState]
  );

  const togglePanel = useCallback(() => {
    if (isOpen) {
      closePanel(true);
      return;
    }
    openPanel();
  }, [closePanel, isOpen, openPanel]);

  useEffect(() => {
    if (!isOpen) return;

    const panel = panelRef.current;
    if (!panel) return;

    const focusable = getFocusable(panel);
    if (focusable.length > 0) {
      focusable[0].focus();
    } else {
      panel.focus();
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closePanel(true);
        return;
      }

      if (event.key !== "Tab") return;

      const currentPanel = panelRef.current;
      if (!currentPanel) return;

      const currentFocusable = getFocusable(currentPanel);
      if (currentFocusable.length === 0) {
        event.preventDefault();
        currentPanel.focus();
        return;
      }

      const first = currentFocusable[0];
      const last = currentFocusable[currentFocusable.length - 1];
      const activeElement = document.activeElement as HTMLElement | null;

      if (event.shiftKey) {
        if (!activeElement || activeElement === first || !currentPanel.contains(activeElement)) {
          event.preventDefault();
          last.focus();
        }
      } else if (activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closePanel, isOpen]);

  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      triggerRef.current?.focus();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (routeKey !== "landing") {
      setCanShowOnLanding(true);
      return;
    }
    if (canShowOnLanding) return;

    const handleScroll = () => {
      if (window.scrollY > APURI_LANDING_SCROLL_THRESHOLD) {
        setCanShowOnLanding(true);
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [canShowOnLanding, routeKey]);

  if (isCheckoutPath) {
    return null;
  }

  const showCompactStarter = !isOpen && !isClosed;
  const canRenderWidget = routeKey !== "landing" || canShowOnLanding;

  return (
    <>
      {canRenderWidget && showCompactStarter && (
        <section
          className="fixed bottom-24 right-4 z-40 w-[min(92vw,20rem)] rounded-2xl border border-border/70 bg-background/95 p-4 shadow-2xl shadow-black/10 backdrop-blur sm:bottom-20"
          aria-label="Apuri aloitustila"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold">Apuri</h2>
              <p className="mt-1 text-xs text-muted-foreground">Autan sinua käytön kanssa.</p>
            </div>
            <button
              type="button"
              onClick={() => closePanel(true)}
              className="rounded-md px-2 py-1 text-[11px] font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Pienennä apuri"
            >
              Pienennä
            </button>
          </div>
          <button
            type="button"
            onClick={openPanel}
            className="mt-3 inline-flex rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            aria-label="Avaa apuri"
          >
            Avaa
          </button>
        </section>
      )}

      {canRenderWidget && (
        <HelpPanel
          isOpen={isOpen}
          onClose={() => closePanel(true)}
          panelRef={panelRef}
        >
          <HelpChatView context={apuriContext} quickQuestions={quickQuestionLabels} />
        </HelpPanel>
      )}

      {canRenderWidget && (isClosed || isOpen) && (
        <div className="fixed bottom-20 right-4 z-40 sm:bottom-6">
          <HelpButton isOpen={isOpen} onClick={togglePanel} buttonRef={triggerRef} />
        </div>
      )}
    </>
  );
}
